import React, { useContext } from "react"

import styled, { css } from "styled-components"
import { useStaticQuery, graphql } from "gatsby"
import { T } from "../shared/typography.styles"

import Breakpoints from "../shared/breakpoints"

import Img from "gatsby-image"
import { UIContext } from "../shared/layout"
import _SolutionsSlides from "../homepage/solutions-slide"
import {
  Block,
  Grid,
  Spacer,
} from "../../design-system/atoms/layout-primitives"

const SectionContainer = styled.div`
  padding: 64px 0 48px;
  border-bottom: 1px solid #2e2e2e27;

  @media (min-width: ${Breakpoints.md}) {
    padding: 64px 0;
  }

  @media (min-width: ${Breakpoints.lg}) {
    padding: 128px 0 96px;
  }
`
const Title = styled.h2`
  ${T.sm.h2.semiBold}
  grid-column: 1 / -1;

  @media (min-width: ${Breakpoints.md}) {
    ${T.md.h2.semiBold}
    grid-column: 3 / -3;
    text-align: center;
  }

  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.h2.semiBold}
    grid-column: 3 / -3;
    text-align: center;
  }

  & > strong {
    color: var(--burnt-orange);
    font-weight: 500;
  }
`

const Description = styled.p`
  ${T.sm.body.regular}
  grid-column: 1 / -1;
  margin-top: 24px;

  &:first-of-type {
    margin-bottom: 16px;
  }

  @media (min-width: ${Breakpoints.md}) {
    ${T.md.body.regular}
    grid-column: 2 / -2;
    text-align: center;
  }

  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.body.regular}
    grid-column: 3 / -3;
    text-align: center;

    &:first-of-type {
      margin-bottom: 0px;
    }
  }
`

const StyledImage = styled(Img)`
  grid-column: 1 / -1;

  &:first-of-type {
    margin-top: 32px;
  }

  @media (min-width: ${Breakpoints.md}) {
    &:first-of-type {
      margin-top: 48px;
      grid-column: 1 / -2;
    }
    margin-top: 32px;
  }
  @media (min-width: ${Breakpoints.lg}) {
    &:first-of-type {
      margin-top: 32px;
      grid-column: 1 / -1;
    }
  }
`

const SolutionsSlides = styled(_SolutionsSlides)`
  margin-bottom: 48px;
`

export default function ApplicationsDiagram() {
  const query = useStaticQuery(graphql`
    query {
      desktop1: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/mapping applications/mapping_applications_desktop.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      tablet1: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/mapping applications/mapping_applications_tablet_1.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1024, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      tablet2: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/mapping applications/mapping_applications_tablet_2.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1024, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobile1: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/mapping applications/sdk_infographic_mobile_1.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobile2: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/mapping applications/sdk_infographic_mobile_2.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobile3: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/mapping applications/sdk_infographic_mobile_3.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobile4: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/mapping applications/sdk_infographic_mobile_4.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const diagramAlts = [
    "Stadiums: Seat Assignment, Parking Vacancy, Venue Wayfinding, Proximity Marketing, Multi-modal Transport, Multi-destination Wayfinding. Transportation: Multi-modal Transport, Multi-destination Wayfinding. REITs & Malls: Leasing Tools, Digital Directory, Web Wayfinding, Paid Search & Ads, Triggered Events & Promotions.",
    "Parking Lots: Parking Vacancy. Construction Projects: Dynamic Document, Asset Tracking.",
    "Mixed Use Spaces: Leasing Tools, Digital Directory, Web & Mobile Wayfinding, Operational Performance, Parking Availability. Retailers: Pick-and-Pack Optimization, Triggered Events & Promotions, Web & Mobile Wayfinding.",
    "Airports: Parking Vacancy, Digital Directory, Web Wayfinding, Operational Performance. Emergency Services: Fire Response, Evacuation Planning, Asset Tracking, Building Plan Annotation.",
  ]

  const {
    state: { breakpoint },
  } = useContext(UIContext)

  return (
    <SectionContainer>
      <Grid>
        <Title>
          Indoor Mapping for <strong>Different Industries</strong>
        </Title>
        <Description>
          Google Maps and Apple Maps can fulfill outdoor mapping needs, but the
          implementation of an indoor mapping platform allows you to accurately
          manage your indoor floorplans. Create a mapping experience for any
          space — airports, retailers, healthcare, travel, stadiums, malls,
          transportation, and more.
        </Description>
      </Grid>
      <Spacer height={{ sm: "32px", md: "48px", lg: "48px" }} />
      <Grid noPadding={breakpoint === "sm"}>
        <Block
          col={{ sm: "1 / -1", md: "2 / -2", lg: "2 / -2" }}
          variants={[
            css`
              & > * {
                padding: 0;
                margin: 0;
              }

              & p {
                font-size: 14px;
                padding: 16px 0;
              }
            `,
          ]}
        >
          <SolutionsSlides />
        </Block>
      </Grid>
      <Spacer height={{ sm: "48px", md: "96px", lg: "128px" }} />
      <Grid>
        <Title>
          Indoor Mapping <strong>Applications</strong>
        </Title>
        <Description>
          Not only does the Mappedin platform provide intuitive indoor
          wayfinding for your visitors, but it can be used to improve
          operational efficiencies and help with informed decision making. Use
          accurate indoor data to assist with Smart Building management,
          emergency route planning, employee onboarding, workspace management,
          and more.
        </Description>

        {breakpoint === "sm" && (
          <>
            <StyledImage
              imgStyle={{ objectFit: "contain" }}
              fluid={query.mobile1.childImageSharp.fluid}
              alt={`Use Cases - ${diagramAlts[0]}`}
            />
            <StyledImage
              imgStyle={{ objectFit: "contain" }}
              fluid={query.mobile2.childImageSharp.fluid}
              alt={`Use Cases - ${diagramAlts[1]}`}
            />
            <StyledImage
              imgStyle={{ objectFit: "contain" }}
              fluid={query.mobile3.childImageSharp.fluid}
              alt={`Use Cases - ${diagramAlts[2]}`}
            />
            <StyledImage
              imgStyle={{ objectFit: "contain" }}
              fluid={query.mobile4.childImageSharp.fluid}
              alt={`Use Cases - ${diagramAlts[3]}`}
            />
          </>
        )}

        {breakpoint === "md" && (
          <>
            <StyledImage
              imgStyle={{ objectFit: "contain" }}
              fluid={query.tablet1.childImageSharp.fluid}
              alt={`Use Cases - ${diagramAlts[0]} ${diagramAlts[1]}`}
            />
            <StyledImage
              imgStyle={{ objectFit: "contain" }}
              fluid={query.tablet2.childImageSharp.fluid}
              alt={`Use Cases - ${diagramAlts[2]} ${diagramAlts[3]}`}
            />{" "}
          </>
        )}
        {breakpoint === "lg" && (
          <>
            <StyledImage
              imgStyle={{ objectFit: "contain" }}
              fluid={query.desktop1.childImageSharp.fluid}
              alt={`Use Cases - ${diagramAlts[0]} ${diagramAlts[1]} ${diagramAlts[2]} ${diagramAlts[3]}`}
            />
          </>
        )}
      </Grid>
    </SectionContainer>
  )
}
